import DialogBox from '../DialogBox';
import ExportOptionsDialog from '../ExportOptionsDialog';
import { GOOGLE_CHARTS_TYPES } from '../../consts/graphs';
import downloadPageAsPDF from '../../helpers/downloadPageAsPDF';
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { GoogleChartWrapperChartType, GoogleDataTableColumn, GoogleDataTableRow } from 'react-google-charts';

// Subcomponents
import ChartHead from './comps/ChartHead';
import ChartRenderer from './comps/ChartRenderer';
import LoadingOverlay from './comps/LoadingOverlay';

// Helpers
import {
  getRAGColor,
  transformDataForRAGColoring,
  hasValidData,
  formatColumnHeaders,
} from './helpers/chartHelpers';

export interface ChartContainerProps {
  title: string;
  index?: number;
  chartId: string;
  endpoint?: string; // API endpoint for export (e.g., '/api/graphs/dashboard-1/most-least-compliant-courses')
  options?: object;
  boxWidth?: string;
  boxHeight?: string;
  className?: string;
  showXlsExport?: boolean;
  selectedVersion?: number;
  rows?: GoogleDataTableRow[];
  style?: React.CSSProperties;
  ragColoringColumns?: number[];
  hideColumnsByIndex?: string[];
  hideSortingOnColumns?: string[];
  styleChart?: React.CSSProperties;
  columns?: GoogleDataTableColumn[];
  AdditionalDialogComp?: JSX.Element;
  onRowSelect?: (props: any) => void;
  toggleFocus?: (index: number) => void;
  onDialogBoxChange?: (isOpen: boolean) => void;
  chartType: GoogleChartWrapperChartType | string;
  otherActions?: { 
    image?: string, 
    text?: string, 
    action?: () => void, 
    style?: React.CSSProperties,
    isDropdown?: boolean,
    dropdownOptions?: { value: string; label: string }[],
    currentValue?: string,
    onSelect?: (value: string) => void
  }[];
  onScroll?: (chartId: any, event: React.WheelEvent<HTMLDivElement>, scrollDirectionZ?: "up" | "down", chartType?: string) => void;
  onSortChange?: (chartId: string, columnId: string, desc: boolean) => void;
  paginationActions?: {
    getPaginationInfo: (chartId: string) => {
      currentPage: number;
      totalPages: number;
      totalRecords: number;
      limit: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startRecord: number;
      endRecord: number;
    } | null;
    handlePaginationChange: (chartId: string, action: 'next' | 'previous' | 'page', pageNumber?: number) => void;
  };
  data?: any;
  total?: number;
  isLoading?: boolean;
  exportActions?: {
    fetchAllDataForExport: (chartId: string, sortBy?: string, sortOrder?: 'asc' | 'desc', signal?: AbortSignal) => Promise<any[] | null>;
  };
  queryParams?: Record<string, any>; // Query parameters for export
}

const ChartContainer: React.FC<ChartContainerProps> = ({
  rows,
  data,
  total,
  style,
  title,
  index,
  chartId,
  endpoint,
  columns,
  options,
  boxWidth,
  onScroll,
  boxHeight,
  className,
  chartType,
  styleChart,
  queryParams,
  exportActions,
  isLoading = false,
  otherActions = [],
  paginationActions,
  onDialogBoxChange,
  hideColumnsByIndex,
  ragColoringColumns,
  hideSortingOnColumns,
  onRowSelect = () => {},
  AdditionalDialogComp = null,
  onSortChange: onSortChangeProp,
  showXlsExport,
}) => {
  const scrollCounter = useRef(0);
  const [isExporting, setIsExporting] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'xls'>('csv');
  const dropdownRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null);
  // State to track selected values for each dropdown (multi-select)
  const [dropdownSelections, setDropdownSelections] = useState<{ [key: number]: Set<string> }>({});
  // State to track initial selections when dropdown opens (to detect changes)
  const [initialDropdownSelections, setInitialDropdownSelections] = useState<{ [key: number]: Set<string> }>({});
  // Ref to track last known currentValue for each dropdown to detect external changes
  const lastCurrentValueRef = useRef<{ [key: number]: string | undefined }>({});
  
  const isTable = String(chartType).toLowerCase() === 'table';
  const isBarChart = chartType === GOOGLE_CHARTS_TYPES.BARCHART || 
    chartType === GOOGLE_CHARTS_TYPES.COLUMNCHART || 
    chartType === GOOGLE_CHARTS_TYPES.BAR;

  // Transform data for RAG coloring
  const transformedData = useMemo(() => {
    const getRAGColorFn = (value: any) => getRAGColor(value, ragColoringColumns);
    return transformDataForRAGColoring(data || [], ragColoringColumns || [], isBarChart, getRAGColorFn);
  }, [data, ragColoringColumns, isBarChart]);

  // Format headers for non-table charts only (on a copy). Tables keep API column
  // names for server-side sortBy and format labels in TableGraph for display.
  const chartDisplayData = useMemo(() => {
    const source = transformedData || data;
    if (!source?.length || isTable) return source;
    const cloned = [
      Array.isArray(source[0]) ? [...source[0]] : source[0],
      ...source.slice(1),
    ];
    formatColumnHeaders(cloned);
    return cloned;
  }, [transformedData, data, isTable]);

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
      
      // Check if click is outside any other action dropdown
      const clickedOutsideAllDropdowns = Object.values(dropdownRefs.current).every(
        ref => !ref || !ref.contains(event.target as Node)
      );
      if (clickedOutsideAllDropdowns && openDropdownIndex !== null) {
        // Call onSelect before closing if it's a dropdown with onSelect and selections changed
        const closingIndex = openDropdownIndex;
        const closingAction = otherActions[closingIndex];
        if (closingAction?.isDropdown && closingAction?.onSelect && closingAction?.dropdownOptions) {
          const selectedValues = dropdownSelections[closingIndex];
          const initialSelections = initialDropdownSelections[closingIndex];
          
          if (selectedValues && initialSelections) {
            // Compare current selections with initial selections
            const currentValues = Array.from(selectedValues).filter(v => v !== 'all').sort();
            const initialValues = Array.from(initialSelections).filter(v => v !== 'all').sort();
            
            // Check if selections have changed
            const hasChanged = currentValues.length !== initialValues.length ||
              !currentValues.every((val, i) => val === initialValues[i]);
            
            if (hasChanged) {
              // Pass comma-separated string of selected values
              const finalValues = currentValues.join(',');
              closingAction.onSelect(finalValues);
              // Update last known value to reflect the final state
              lastCurrentValueRef.current[closingIndex] = finalValues || undefined;
            }
          }
        }
        // Clear initial selections
        if (openDropdownIndex !== null) {
          setInitialDropdownSelections(prev => {
            const updated = { ...prev };
            delete updated[openDropdownIndex];
            return updated;
          });
        }
        setOpenDropdownIndex(null);
      }
    };

    if (showExportDropdown || openDropdownIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportDropdown, openDropdownIndex, otherActions, dropdownSelections, initialDropdownSelections]);

  const handleExportDialogClose = () => {
    setShowExportDialog(false);
  };

  const handlePdfExport = async () => {
    if (isExporting) return;
    setShowExportDropdown(false);
    setIsExporting(true);
    try {
      await downloadPageAsPDF(chartId, `${chartId}${isDialogOpen ? "-popup" : ""}`);
    } catch (error) {
      console.error('PDF export failed:', error);
      alert('❌ PDF export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCsvExport = () => {
    if (isExporting) return;
    setExportFormat('csv');
    setShowExportDropdown(false);
    setShowExportDialog(true);
  };

  const handleXlsExport = () => {
    if (isExporting) return;
    setExportFormat('xls');
    setShowExportDropdown(false);
    setShowExportDialog(true);
  };

  const handleToggleDialog = () => {
    setIsDialogOpen(!isDialogOpen);
    if (onDialogBoxChange) onDialogBoxChange(!isDialogOpen);
  };

  const handleDropdownToggle = (index: number) => {
    if (index === -1) {
      setOpenDropdownIndex(null);
    } else {
      setOpenDropdownIndex(openDropdownIndex === index ? null : index);
    }
  };

  const paginationInfo = paginationActions?.getPaginationInfo(chartId || '');
  
  const onScrollAction = (scroll: React.WheelEvent<HTMLDivElement>) => {
    if (isTable) return;
    scrollCounter.current++;
    if (scrollCounter.current % 2 === 0) {
      onScroll?.(chartId || "", scroll, scroll?.deltaY > 0 ? "down" : "up", chartType as string);
    }
  };

  const isValidData = hasValidData(rows, columns, data);

  return (
    <>
      <div
        key={index}
        onWheel={onScrollAction}
        className={`chart-container ${chartId} ${isTable ? 'chart-container--table' : ''}`}
        style={{ 
          height: boxHeight, 
          position: 'relative',
          ...style,  
          ...(boxWidth ? { flex: `1 1 ${boxWidth}` } : {}) 
        }}
      >
        {!isDialogOpen && (
          <ChartHead
            title={title}
            isExporting={isExporting}
            otherActions={otherActions}
            dropdownRefs={dropdownRefs}
            isDialogOpen={isDialogOpen}
            onPdfExport={handlePdfExport}
            onCsvExport={handleCsvExport}
            onXlsExport={handleXlsExport}
            showXlsExport={showXlsExport}
            onToggleDialog={handleToggleDialog}
            exportDropdownRef={exportDropdownRef}
            openDropdownIndex={openDropdownIndex}
            showExportDropdown={showExportDropdown}
            onDropdownToggle={handleDropdownToggle}
            dropdownSelections={dropdownSelections}
            lastCurrentValueRef={lastCurrentValueRef}
            setDropdownSelections={setDropdownSelections}
            initialDropdownSelections={initialDropdownSelections}
            setInitialDropdownSelections={setInitialDropdownSelections}
            onToggleExportDropdown={() => setShowExportDropdown(!showExportDropdown)}
          />
        )}
        <ChartRenderer
          rows={rows}
          title={title}
          chartId={chartId}
          isTable={isTable}
          columns={columns}
          options={options}
          onScroll={onScroll}
          className={className}
          chartType={chartType}
          styleChart={styleChart}
          onRowSelect={onRowSelect}
          hasValidData={isValidData}
          data={chartDisplayData}
          onSortChange={onSortChangeProp}
          ragColoringColumns={ragColoringColumns}
          hideColumnsByIndex={hideColumnsByIndex}
          hideSortingOnColumns={hideSortingOnColumns}
          paginationInfo={paginationInfo || undefined}
          onPaginationChange={(action, pageNumber) => {
            paginationActions?.handlePaginationChange(chartId || '', action, pageNumber);
          }}
        />
        <LoadingOverlay isLoading={isLoading} />
      </div>

      <DialogBox
        hideButtons={true}
        isOpen={isDialogOpen}
        className={`${chartId}-popup`}
        freeDialogHeight={!!AdditionalDialogComp}
        onClose={() => {
          setIsDialogOpen(false);
          if (onDialogBoxChange) onDialogBoxChange(false);
        }}
      >
        <>
          <div
            onWheel={onScrollAction}
            style={{ 
              position: 'relative',
              ...(AdditionalDialogComp ? { padding: "20px" } : {})
            }}
            className={`dialog-content ${AdditionalDialogComp ? "additional-component" : ""}`}
          >
            <ChartHead
              title={title}
              isExporting={isExporting}
              isDialogOpen={isDialogOpen}
              otherActions={otherActions}
              dropdownRefs={dropdownRefs}
              onPdfExport={handlePdfExport}
              onCsvExport={handleCsvExport}
              onXlsExport={handleXlsExport}
              showXlsExport={showXlsExport}
              onToggleDialog={handleToggleDialog}
              openDropdownIndex={openDropdownIndex}
              exportDropdownRef={exportDropdownRef}
              showExportDropdown={showExportDropdown}
              onDropdownToggle={handleDropdownToggle}
              dropdownSelections={dropdownSelections}
              lastCurrentValueRef={lastCurrentValueRef}
              setDropdownSelections={setDropdownSelections}
              initialDropdownSelections={initialDropdownSelections}
              setInitialDropdownSelections={setInitialDropdownSelections}
              onToggleExportDropdown={() => setShowExportDropdown(!showExportDropdown)}
            />
            <ChartRenderer
              rows={rows}
              title={title}
              isTable={isTable}
              chartId={chartId}
              columns={columns}
              options={options}
              onScroll={onScroll}
              className={className}
              chartType={chartType}
              styleChart={styleChart}
              onRowSelect={onRowSelect}
              hasValidData={isValidData}
              data={chartDisplayData}
              onSortChange={onSortChangeProp}
              ragColoringColumns={ragColoringColumns}
              hideColumnsByIndex={hideColumnsByIndex}
              hideSortingOnColumns={hideSortingOnColumns}
              paginationInfo={paginationInfo || undefined}
              onPaginationChange={(action, pageNumber) => {
                paginationActions?.handlePaginationChange(chartId || '', action, pageNumber);
              }}
            />
            <LoadingOverlay isLoading={isLoading} />
          </div>
          {AdditionalDialogComp}
        </>
      </DialogBox>
      <ExportOptionsDialog
        data={data}
        total={total}
        index={index}
        chartId={chartId}
        endpoint={endpoint}
        queryParams={queryParams}
        isOpen={showExportDialog}
        exportActions={exportActions}
        onClose={handleExportDialogClose}
        paginationInfo={paginationInfo || null}
        exportFormat={exportFormat}
        ragColoringColumns={ragColoringColumns}
      />
    </>
  );
};

export default ChartContainer;
